import { replayFreeSolution } from '../model/free-replay';
import { layoutSignature, signatureHash } from './free-generator';
import { ROUTE_CACHE_CAP } from '../save/block-jam-save-data';
/**
 * Module 87 (WP-87.2b) — every route the worker finds, kept so no board is
 * searched twice: not after a restart, not after a track switch, not after a
 * reload (the entries ride in the save, written by `_commitState`).
 *
 * Keyed by the WHOLE board, not by `layoutSignature` alone: that signature is
 * geometry (cells, gates, plates) and says nothing about which block id or
 * colour sits where, while a route is a list of block ids. And a hit is
 * REPLAYED before it is trusted — a route that does not clear this exact board
 * is dropped and the board is searched as if the cache were empty — so a stale
 * or colliding entry costs one search, never a wrong solution.
 */
/** Oldest entries go first once the cache is full; ~90 bytes each in the save. */
export { ROUTE_CACHE_CAP };
const ENCODE = {
    up: 'U',
    down: 'D',
    left: 'L',
    right: 'R',
};
const DECODE = { U: 'up', D: 'down', L: 'left', R: 'right' };
/** The cache key for a board: geometry plus which block (id, colour, lock) is where. */
export function boardRouteKey(state) {
    const { blocks, puzzle } = state;
    const who = blocks
        .map((b) => `${b.id}:${b.colorId}:${b.movementConstraint ?? 'free'}:${b.position.row}.${b.position.col}`)
        .sort()
        .join('/');
    const gateColours = puzzle.exits
        .map((e) => `${e.direction}:${e.position.row}.${e.position.col}:${e.colorId ?? ''}`)
        .sort()
        .join('/');
    return signatureHash(`${puzzle.gridSize}#${layoutSignature(blocks, puzzle.exits, puzzle.plates)}#${who}#${gateColours}`);
}
export function encodeRoute(moves) {
    return moves.map((m) => `${m.blockId}${ENCODE[m.direction]}`).join(' ');
}
export function decodeRoute(encoded) {
    if (encoded.length === 0)
        return null;
    const moves = [];
    for (const token of encoded.split(' ')) {
        const direction = DECODE[token.slice(-1)];
        const blockId = token.slice(0, -1);
        if (!direction || blockId.length === 0)
            return null;
        moves.push({ blockId, direction });
    }
    return moves;
}
function clears(state, moves) {
    const { exits, gridSize, plates } = state.puzzle;
    const replay = replayFreeSolution(state.blocks, moves, exits, gridSize, {}, plates);
    return replay.invalidAt === null && replay.cleared;
}
export class RouteCache {
    constructor(entries) {
        this._entries = new Map();
        for (const [key, route] of entries ?? [])
            this._entries.set(key, route);
        this._trim();
    }
    /** The cached route for this exact board, or null — never a route that does not clear it. */
    get(state) {
        const key = boardRouteKey(state);
        const encoded = this._entries.get(key);
        if (encoded === undefined)
            return null;
        const moves = decodeRoute(encoded);
        if (moves && clears(state, moves))
            return moves;
        this._entries.delete(key);
        return null;
    }
    put(state, moves) {
        if (moves.length === 0)
            return;
        const key = boardRouteKey(state);
        // Re-inserting moves the entry to the newest end.
        this._entries.delete(key);
        this._entries.set(key, encodeRoute(moves));
        this._trim();
    }
    get size() {
        return this._entries.size;
    }
    /** Oldest first, for the save. */
    entries() {
        return [...this._entries];
    }
    _trim() {
        while (this._entries.size > ROUTE_CACHE_CAP) {
            const oldest = this._entries.keys().next().value;
            this._entries.delete(oldest);
        }
    }
}
